import React, { useState, useEffect } from 'react';
import { useDevelopers } from '../hooks/useDevelopers';
import DeveloperTable from './DeveloperTable';
import DeveloperDetailsModal from './DeveloperDetailsModal';
import { DeveloperFilters, Developer } from '../types/developer';

type ViewMode = 'tiles' | 'table';

interface Props {
  selectedDevelopers: Developer[];
  onSelectDeveloper: (developer: Developer) => void;
  onDeselectDeveloper: (developerId: string) => void;
  onSelectAll: (developers: Developer[]) => void;
  onDeselectAll: () => void;
  onClearSelection: () => void;
  headerSearchTerm?: string;
  highlightSelection?: boolean;
}

const DeveloperList: React.FC<Props> = ({
  selectedDevelopers,
  onSelectDeveloper,
  onDeselectDeveloper,
  onSelectAll,
  onDeselectAll,
  onClearSelection,
  headerSearchTerm = '',
  highlightSelection = false
}) => {
  const { 
    developers, 
    // allDevelopers, 
    loading, 
    error, 
    // refetch, 
    setSearchTerm, 
    setFilters
  } = useDevelopers();
  
  const [/* activeFiltersCount */, setActiveFiltersCount] = useState(0);
  const [/* currentView */, setCurrentView] = useState<ViewMode>('table'); // Changed default to table
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [modalDeveloper, setModalDeveloper] = useState<Developer | null>(null);

  // Sync header search term with useDevelopers hook
  useEffect(() => {
    setSearchTerm(headerSearchTerm);
  }, [headerSearchTerm, setSearchTerm]);

  const handleFiltersChange = (filters: DeveloperFilters) => {
    const count = (
      filters.skills.length +
      filters.experienceLevel.length +
      filters.availability.length +
      (filters.hourlyRateRange.min > 10 || filters.hourlyRateRange.max < 100 ? 1 : 0)
    );
    setActiveFiltersCount(count);
    setFilters(filters);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unusedFiltersChange = handleFiltersChange;

  const handleRefresh = () => {
    window.location.reload();
  };

  // Updated to use props handlers
  const handleSelectAllDevelopers = () => {
    onSelectAll(developers);
  };

  const handleViewChange = (view: ViewMode) => {
    setCurrentView(view);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unusedViewChange = handleViewChange;

  const handleOpenDetails = (developer: Developer) => {
    try {
      console.log('Opening modal for developer:', developer?.name, developer?.id);
      if (!developer || !developer.id || !developer.name) {
        console.error('Invalid developer object passed to modal:', developer);
        return;
      }
      setModalDeveloper(developer);
      setDetailsModalOpen(true);
    } catch (error) {
      console.error('Error opening developer details modal:', error);
    }
  };
  
  const handleCloseDetails = () => {
    try {
      console.log('Closing modal');
      setDetailsModalOpen(false);
      setModalDeveloper(null);
    } catch (error) {
      console.error('Error closing developer details modal:', error);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Developers</h2>
          <p className="text-gray-600 mb-4">Unable to load developer data. Please try again.</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Developer Table Section - Responsive height */}
      <div>
        <div className="max-w-6xl mx-auto border-t border-b border-gray-200">
          <DeveloperTable
            developers={developers}
            selectedDevelopers={selectedDevelopers}
            onSelectDeveloper={onSelectDeveloper}
            onDeselectDeveloper={onDeselectDeveloper}
            onSelectAll={handleSelectAllDevelopers}
            onDeselectAll={onDeselectAll}
            isLoading={loading}
            onRowClick={handleOpenDetails}
            highlightSelection={highlightSelection}
          />
        </div>
      </div>

      {/* Developer Details Modal */}
      <DeveloperDetailsModal
        developer={modalDeveloper}
        open={detailsModalOpen}
        onClose={handleCloseDetails}
        onSelectDeveloper={onSelectDeveloper}
        isSelected={modalDeveloper ? selectedDevelopers.some(d => d.id === modalDeveloper.id) : false}
      />
    </div>
  );
};

export default DeveloperList; 